import React from "react";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

import * as Sentry from "@sentry/react";

import { getBuildId } from "@features/appVersion/deployVersionGuard";
import isMobile from "@utils/mobileDetect";

Sentry.init({
  dsn: "https://ac0d29b5b0eabf6b29a0492720cc93b8@o555075.ingest.us.sentry.io/4508648373026816",
  environment: import.meta.env.VITE_DEV_ENV,
  initialScope: {
    tags: {
      app_type: isMobile ? "mobile" : "desktop",
      build_id: getBuildId() ?? "unknown",
    },
  },
  integrations: [
    Sentry.globalHandlersIntegration(),
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  normalizeDepth: 5,
  tracesSampleRate: 1.0,
  tracePropagationTargets: [/^\/api/],
  sendDefaultPii: true,
  denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
  beforeSend(event, hint) {
    const original = hint?.originalException as any;
    const code = original?.code ?? original?.message ?? "";
    const status = original?.response?.status;

    // drop aborted/canceled and 401 auth flow noise
    const isAbort = /AbortError|ECONNABORTED|ERR_CANCELED|ERR_NETWORK/i.test(
      code
    );
    if (isAbort || status === 401) return null;

    return event;
  },
  enabled: import.meta.env.VITE_DEV_ENV !== "dev",
});

import("@sentry/react").then((lazyLoadedSentry) => {
  Sentry.addIntegration(
    lazyLoadedSentry.replayIntegration({
      maskAllText: false,
    })
  );
});

export const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);
